(ns graph-ui.web.graph
  "THE :graph WIDGET — a subgraph, drawn.

  ;; @feature F-gi-v3-010-01-graph-ui-poc
  ;; @adr     ADR-graph-ui-v0-009-01-rich-theming-as-data
  ;; @adr     ADR-graph-ui-v0-019-01-design-system-is-the-source
  ;; @concept CONCEPT-graph-ui-v0-003-01-widget-tree
  ;; @spec    SPEC-alatus-v0-002-01-ikc1-functional-target
  ;; @plan    PLAN-ikc-v3-073-01-the-graph-you-can-look-at

  George, 2026-09-04: reader-v2 and reader-v3 *\"still need months of work to get
  to the ikc1 functionality\"*. A relationship graph is one of the things ikc1
  had and ikc3 had not. This draws it.

  ── NO GRAPH LIBRARY, AND THAT IS THE DECISION ─────────────────────────────
  ikc1 needed `d3-force` and React Flow for this picture. Here the layouts are
  arithmetic over the node and edge lists, with no dependency. Three reasons,
  in order:

    1. A library is a second vocabulary beside the declared one. Its node map,
       its edge map and its options would become the thing a description has to
       be translated INTO, and the translation is where a declaration stops
       being the source.
    2. Every island's bundle would carry it, including the ones that never draw
       a graph.
    3. The picture is bounded by its own query.

  ── AMENDMENT, 2026-10-13: A CANVAS, NOT SVG (lane IKC3/PLAN-ikc-v3-073-01/GV) ─
  The island graph draws up to 2,000 nodes. Measured 2026-10-13 in headless
  Chrome on kubu24-dev-01: an SVG frame of pan costs 113 ms at 2,000 nodes, a canvas
  frame 26 ms, and sigma.js on this host (WebGL in software) 462 ms. So the
  picture is PAINTED on one canvas, by the code below. The declaration
  (meta-ui v2.1-SNAPSHOT graph.edn) holds the whole table.

  THE SCENE IS DATA, AND THE CANVAS PAINTS THE SAME STYLES. `scene` gives every
  mark with its position and its style. The tests read it. The canvas reads the
  same style functions and the same simulation.

  THE LAYOUT RUNS IN ANIMATION FRAMES above 300 nodes, so the first frame comes
  at once and the picture settles while the person looks at it. Up to 300 nodes
  it runs to its end before the first frame, as before.

  ── THE ENCODING IS A TABLE, NOT CODE ──────────────────────────────────────
  Which colour a page is, which stroke a wikilink gets, what a derived edge
  looks like — all of it is read from dsl/design/graph-encoding.edn through the
  generated mirror. This namespace holds no colour of its own. A canvas cannot
  read a CSS variable, so each token is read from the computed style of the
  canvas when it paints. The fallbacks below are used only when a token has no
  value on the page."
  (:require [reagent.core :as r]
            [clojure.string :as str]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.generated-design :as design]))

;; ---------------------------------------------------------------------------
;; The encoding table
;; ---------------------------------------------------------------------------

(def ^:private encoding
  "dsl/design/graph-encoding.edn's cells, as the renderer sees them. Read once —
   the mirror is a compile-time constant, so there is nothing to invalidate."
  (get-in design/components [:graph-encoding :draws :cells]))

(defn- token-var
  "A token name → the CSS custom property that carries it. Symbols, because that
   is what a design declaration holds (`accent`, `stroke-hairline`)."
  [t]
  (when t (str "var(--" (name t) ")")))

(defn- cell
  "One row of the encoding table, by name, falling back to the table's own
   default row. A type the table has no row for is DRAWN, never hidden — a type
   nobody thought about is exactly what a person should be able to see."
  [prefix k default-key]
  (or (get encoding (symbol (str prefix "/" (name (or k :none)))))
      (get encoding (symbol (str prefix "/" (name default-key))))))

(defn node-style
  "The colour token, the shape and the label size for one node."
  [node]
  (let [c (if (:focus? node)
            (get encoding 'node/focus)
            (cell "node" (:frame-type node) :default))]
    {:tone  (:tone c)
     :fill  (token-var (:tone c))
     :shape (or (:shape c) :circle)
     :type  (:type c)}))

(defn edge-style
  "The colour token, the width token and the dash for one edge.

   THE THREE STATES, IN THE ORDER THE TABLE STATES THEM. A relation's own row
   gives the colour and a dash; `edge/derived` and `edge/inferred` then override
   the dash only, so a derived wikilink keeps the wikilink's colour and says it
   was computed."
  [edge]
  (let [base (cell "edge" (:relation edge) :default)
        dash (cond (:inferred? edge) (:dash (get encoding 'edge/inferred))
                   (:derived? edge)  (:dash (get encoding 'edge/derived))
                   :else             (:dash base))]
    {:tone   (:tone base)
     :rule   (:rule base)
     :stroke (token-var (:tone base))
     :width  (token-var (:rule base))
     :dash   (case dash
               :dashed [5 4]
               :dotted [1 4]
               nil)}))

;; ---------------------------------------------------------------------------
;; Layouts — three, and the relation picks which
;; ---------------------------------------------------------------------------

(def ^:private ring-gap
  "How far one hop is from the next, in the picture's own units. The camera
   scales to the content, so this is a ratio and never a pixel count."
  120)

(defn- radial
  "RINGS BY HOP, the focus at the centre. The ring IS the distance, so nothing
   has to be inferred from how long a line happens to be. Each ring is turned
   by half a step against the one inside it so spokes do not line up."
  [nodes]
  (let [by-hop (group-by :hops nodes)]
    (into {}
          (for [[hop ring] by-hop
                :let [n (count ring)
                      rr (* hop ring-gap)
                      turn (if (odd? hop) (/ js/Math.PI n) 0)]
                [i node] (map-indexed vector ring)]
            [(:id node)
             (if (zero? hop)
               {:x 0 :y 0}
               (let [a (+ turn (* i (/ (* 2 js/Math.PI) n)))]
                 {:x (* rr (js/Math.cos a))
                  :y (* rr (js/Math.sin a))}))]))))

(defn- tree
  "LEFT TO RIGHT BY HOP — a column per ring, nodes stacked inside it. For a
   relation that IS a hierarchy."
  [nodes]
  (let [by-hop (group-by :hops nodes)]
    (into {}
          (for [[hop ring] by-hop
                :let [n (count ring)
                      h (* ring-gap 0.9)]
                [i node] (map-indexed vector ring)]
            [(:id node)
             {:x (* hop ring-gap 1.6)
              :y (* h (- i (/ (dec n) 2)))}]))))

(defn- spiral
  "A SUNFLOWER, in the order of the nodes. A set scope gives each node :hops 1,
   so a ring would put all of them on one circle. The query orders a set scope
   by degree, most first, so the most connected nodes start at the centre."
  [nodes]
  (let [ga (* js/Math.PI (- 3 (js/Math.sqrt 5)))]
    (into {}
          (map-indexed (fn [i node]
                         (let [rr (* ring-gap 0.35 (js/Math.sqrt (inc i)))]
                           [(:id node) {:x (* rr (js/Math.cos (* i ga)))
                                        :y (* rr (js/Math.sin (* i ga)))}])))
          nodes)))

(def set-scopes #{:partition :frame-type})

(defn layout-for
  "THE LAYOUT OF ONE PICTURE. The person's choice inside the bound value wins
   (T-web-graph-008). Then a set scope draws :force, because each of its nodes
   has :hops 1 and :radial would put all of them on one ring. Then the
   description's own :layout, then :radial."
  [subgraph props-layout]
  (or (:layout subgraph)
      (when (set-scopes (:scope subgraph)) :force)
      props-layout
      :radial))

;; ---------------------------------------------------------------------------
;; The force simulation — typed arrays, so 2,000 nodes stay fast
;; ---------------------------------------------------------------------------

(def ^:private spring (* ring-gap 0.8))

(def sync-limit
  "Up to this many nodes the simulation runs to its end before the first frame.
   Above it the simulation runs in animation frames."
  300)

(defn- iterations-for [n]
  (cond (<= n sync-limit) 200 (<= n 1000) 180 :else 150))

(defn make-sim
  "A FRUCHTERMAN-REINGOLD SIMULATION over typed arrays. Seeded and not random:
   the same data gives the same picture, so a reader can recognise what they
   were just looking at. `seed` is a map id → {:x :y}. The nodes in `pinned`
   stay where the seed puts them."
  [nodes edges seed pinned]
  (let [n     (count nodes)
        index (into {} (map-indexed (fn [i nd] [(:id nd) i])) nodes)
        xs    (js/Float64Array. n)
        ys    (js/Float64Array. n)
        pin   (js/Uint8Array. n)
        pairs (into [] (comp (map (fn [e] [(get index (:from e)) (get index (:to e))]))
                             (filter (fn [[a b]] (and a b (not= a b)))))
                    edges)
        es    (js/Int32Array. (* 2 (count pairs)))]
    (doseq [[i nd] (map-indexed vector nodes)]
      (let [p (get seed (:id nd) {:x 0 :y 0})]
        (aset xs i (:x p)) (aset ys i (:y p))
        (when (contains? pinned (:id nd)) (aset pin i 1))))
    (doseq [[j [a b]] (map-indexed vector pairs)]
      (aset es (* 2 j) a) (aset es (inc (* 2 j)) b))
    #js {:n n :x xs :y ys :pin pin :es es
         :dx (js/Float64Array. n) :dy (js/Float64Array. n)
         :step 0 :iters (iterations-for n)
         ;; A DENSE PICTURE PULLS LESS. With many more edges than nodes the
         ;; springs win over the repulsion and every node falls into one ball.
         ;; So the pull of one edge is scaled by the nodes for each edge.
         :pull (min 1 (/ (* 2 (max 1 n)) (max 1 (count pairs))))
         :index index}))

(defn- repel-exact!
  "Every pair, exactly. For a small picture it is the cheapest correct answer."
  [^js sim k2]
  (let [n (.-n sim) xs (.-x sim) ys (.-y sim) dx (.-dx sim) dy (.-dy sim)]
    (dotimes [i n]
      (let [xi (aget xs i) yi (aget ys i)]
        (loop [j (inc i)]
          (when (< j n)
            (let [ddx (- xi (aget xs j)) ddy (- yi (aget ys j))
                  d2  (+ (* ddx ddx) (* ddy ddy) 0.01)
                  f   (/ k2 d2)
                  fx  (* ddx f) fy (* ddy f)]
              (aset dx i (+ (aget dx i) fx)) (aset dy i (+ (aget dy i) fy))
              (aset dx j (- (aget dx j) fx)) (aset dy j (- (aget dy j) fy))
              (recur (inc j)))))))))

(defn- extent
  "[min max] of the first n values of a typed array."
  [xs n]
  (loop [i 0 lo js/Infinity hi (- js/Infinity)]
    (if (< i n)
      (let [v (aget xs i)] (recur (inc i) (min lo v) (max hi v)))
      [lo hi])))

(defn- repel-grid!
  "A GRID, for a large picture. The plane is cut into cells. A node repels the
   nodes of its own cell and of the eight cells around it exactly, and every
   other cell as one mass at its centre. For 2,000 nodes this is about a
   quarter of the work of every pair."
  [^js sim k2]
  (let [n (.-n sim) xs (.-x sim) ys (.-y sim) dx (.-dx sim) dy (.-dy sim)
        g (max 4 (min 40 (js/Math.ceil (/ (js/Math.sqrt n) 2))))
        [x0 x1] (extent xs n) [y0 y1] (extent ys n)
        cw (/ (+ 1e-6 (- x1 x0)) g) ch (/ (+ 1e-6 (- y1 y0)) g)
        cells (* g g)
        cell-of (js/Int32Array. n)
        cnt  (js/Int32Array. cells)
        mx   (js/Float64Array. cells)
        my   (js/Float64Array. cells)
        cellx (js/Int32Array. cells)
        celly (js/Int32Array. cells)
        start (js/Int32Array. (inc cells))
        items (js/Int32Array. n)
        fill  (js/Int32Array. cells)]
    (dotimes [i n]
      (let [cx (min (dec g) (js/Math.floor (/ (- (aget xs i) x0) cw)))
            cy (min (dec g) (js/Math.floor (/ (- (aget ys i) y0) ch)))
            c  (+ cx (* cy g))]
        (aset cell-of i c)
        (aset cnt c (inc (aget cnt c)))
        (aset mx c (+ (aget mx c) (aget xs i)))
        (aset my c (+ (aget my c) (aget ys i)))))
    (dotimes [c cells]
      (aset start (inc c) (+ (aget start c) (aget cnt c)))
      (aset cellx c (mod c g))
      (aset celly c (quot c g)))
    (dotimes [i n]
      (let [c (aget cell-of i)]
        (aset items (+ (aget start c) (aget fill c)) i)
        (aset fill c (inc (aget fill c)))))
    (dotimes [i n]
      (let [xi (aget xs i) yi (aget ys i)
            c  (aget cell-of i)
            cx (aget cellx c) cy (aget celly c)]
        (dotimes [d cells]
          (let [m (aget cnt d)]
            (when (pos? m)
              (let [ex (aget cellx d) ey (aget celly d)]
                (if (and (<= (js/Math.abs (- ex cx)) 1) (<= (js/Math.abs (- ey cy)) 1))
                  (loop [p (aget start d)]
                    (when (< p (aget start (inc d)))
                      (let [j (aget items p)]
                        (when-not (== i j)
                          (let [ddx (- xi (aget xs j)) ddy (- yi (aget ys j))
                                d2  (+ (* ddx ddx) (* ddy ddy) 0.01)
                                f   (/ k2 d2)]
                            (aset dx i (+ (aget dx i) (* ddx f)))
                            (aset dy i (+ (aget dy i) (* ddy f))))))
                      (recur (inc p))))
                  (let [ddx (- xi (/ (aget mx d) m)) ddy (- yi (/ (aget my d) m))
                        d2  (+ (* ddx ddx) (* ddy ddy) 0.01)
                        f   (/ (* k2 m) d2)]
                    (aset dx i (+ (aget dx i) (* ddx f)))
                    (aset dy i (+ (aget dy i) (* ddy f)))))))))))))

(defn step!
  "Run `k` steps of the simulation, or fewer when it is at its end. Returns
   true while steps remain."
  [^js sim k]
  (let [n (.-n sim) xs (.-x sim) ys (.-y sim) dx (.-dx sim) dy (.-dy sim)
        pin (.-pin sim) es (.-es sim) iters (.-iters sim)
        pull (.-pull sim)
        k2 (* spring spring)]
    (loop [s 0]
      (when (and (< s k) (< (.-step sim) iters))
        (.fill dx 0) (.fill dy 0)
        (if (<= n sync-limit) (repel-exact! sim k2) (repel-grid! sim k2))
        (loop [j 0]
          (when (< j (.-length es))
            (let [a (aget es j) b (aget es (inc j))
                  ddx (- (aget xs a) (aget xs b)) ddy (- (aget ys a) (aget ys b))
                  d   (+ 0.01 (js/Math.sqrt (+ (* ddx ddx) (* ddy ddy))))
                  f   (* pull (/ d spring))]
              (aset dx a (- (aget dx a) (* ddx f))) (aset dy a (- (aget dy a) (* ddy f)))
              (aset dx b (+ (aget dx b) (* ddx f))) (aset dy b (+ (aget dy b) (* ddy f))))
            (recur (+ j 2))))
        (let [temp (+ 0.5 (* spring 3 (- 1 (/ (.-step sim) iters))))]
          (dotimes [i n]
            (when (zero? (aget pin i))
              ;; a weak pull home, so a node with no edge does not drift away
              (let [fx (- (aget dx i) (* 0.02 (aget xs i)))
                    fy (- (aget dy i) (* 0.02 (aget ys i)))
                    l  (+ 1e-9 (js/Math.sqrt (+ (* fx fx) (* fy fy))))
                    sc (/ (min l temp) l)]
                (aset xs i (+ (aget xs i) (* fx sc)))
                (aset ys i (+ (aget ys i) (* fy sc)))))))
        (set! (.-step sim) (inc (.-step sim)))
        (recur (inc s))))
    (< (.-step sim) iters)))

(defn running? [^js sim] (boolean (and sim (< (.-step sim) (.-iters sim)))))

(defn- sim-positions [^js sim nodes]
  (into {} (map-indexed (fn [i nd] [(:id nd) {:x (aget (.-x sim) i) :y (aget (.-y sim) i)}]))
        nodes))

(defn sim-for
  "The simulation of one picture. :radial and :tree are computed at once and
   need no steps. :force is seeded from the rings (ego) or the sunflower (a
   set scope), with the focus pinned at the centre."
  [layout nodes edges]
  (case layout
    :tree   (let [^js s (make-sim nodes [] (tree nodes) #{})] (set! (.-iters s) 0) s)
    :radial (let [^js s (make-sim nodes [] (radial nodes) #{})] (set! (.-iters s) 0) s)
    (let [ego?   (some :focus? nodes)
          seed   (if ego? (radial nodes) (spiral nodes))
          pinned (into #{} (comp (filter :focus?) (map :id)) nodes)]
      (make-sim nodes edges seed pinned))))

(defn positions
  "PURE. The layout, run to its end: id → {:x :y}. The tests read this."
  [layout nodes edges]
  (let [s (sim-for layout nodes edges)]
    (while (step! s 50))
    (sim-positions s nodes)))

;; ---------------------------------------------------------------------------
;; The scene — every mark, as data
;; ---------------------------------------------------------------------------

(def dash-limit
  "Above this many links the canvas paints every link solid. Measured
   2026-10-13 in headless Chrome on kubu24-dev-01 (no GPU): 8,000 dashed
   links cost 233 ms for each frame, and 8,000 solid links 82 ms. The legend
   says so when it happens."
  3000)

(def ^:private node-radius 7)
(def ^:private focus-radius 11)

(defn- radius-of [nd] (if (:focus? nd) focus-radius node-radius))

(defn scene
  "PURE. Every mark of one picture: a node with its position and its style, and
   an edge with its two ends and its style. An edge into a node that is not in
   the picture is not a mark. The tests read this, and the canvas paints with
   the same style functions."
  [subgraph props-layout]
  (let [{:keys [nodes edges]} subgraph
        layout (layout-for subgraph props-layout)
        pos    (positions layout nodes edges)]
    {:layout layout
     :nodes  (vec (for [nd nodes :let [p (get pos (:id nd))]]
                    (merge (node-style nd)
                           {:id (:id nd) :x (:x p) :y (:y p) :r (radius-of nd)})))
     :edges  (vec (for [e edges
                        :let [a (get pos (:from e)) b (get pos (:to e))]
                        :when (and a b)]
                    (merge (edge-style e)
                           {:x1 (:x a) :y1 (:y a) :x2 (:x b) :y2 (:y b)})))}))

;; ---------------------------------------------------------------------------
;; The words under the picture
;; ---------------------------------------------------------------------------

(defn- fmt [n]
  (let [s (str n)]
    (if (< (count s) 4) s
        (str/replace s #"\B(?=(\d{3})+(?!\d))" ","))))

(defn- plural [n word] (str (fmt n) " " word (when (not= 1 n) "s")))

(defn caption-text
  "What the pane says it is showing.

   A PICTURE OF ELEVEN NODES OUT OF FIFTY THOUSAND HAS TO NAME ITS OWN SCOPE.
   Without this line a reader reads the picture as the whole graph. A set scope
   says what it is and why it cut: it keeps the nodes with the most links."
  [{:keys [count edge-count depth truncated? omitted scope in-partition frame-type
           cut-by elapsed-ms]}]
  (let [set? (set-scopes scope)]
    (str (plural (or count 0) "node") " · " (plural (or edge-count 0) "link")
         (cond
           (= :partition scope)  (str " · partition " (some-> in-partition name))
           (= :frame-type scope) (str " · type " (some-> frame-type name))
           :else                 (str " · " (plural (or depth 0) "hop")))
         (when truncated?
           (str " · " (fmt omitted) " more not shown"
                (cond
                  (or (= :degree cut-by) set?)
                  (str ": the " (fmt count) " with the most links are kept")
                  (= :ring cut-by)
                  ": the next ring did not fit")))
         (when elapsed-ms (str " · query " (fmt elapsed-ms) " ms")))))

(defn search-matches
  "PURE. The nodes whose label holds the text, at most `limit`, in the order of
   the picture. Case does not matter. An empty text finds nothing."
  [nodes text limit]
  (let [q (str/lower-case (str/trim (str text)))]
    (if (str/blank? q)
      []
      (into [] (comp (filter #(str/includes? (str/lower-case (str (:label %))) q))
                     (take limit))
            nodes))))

(defn- legend-rows
  "One row per kind actually on the picture. ONLY WHAT IS DRAWN."
  [nodes edges]
  (let [types (distinct (keep :frame-type nodes))
        rels  (distinct (map :relation edges))]
    (concat
      (for [t types]
        {:kind :node :label (name t) :style (node-style {:frame-type t})})
      (for [rl rels]
        {:kind :edge :label (name rl) :style (edge-style {:relation rl})}))))

(defn- shape-svg
  "A legend mark. The legend stays SVG: it holds a few marks, and SVG resolves
   the CSS variables by itself."
  [shape x y rr fill]
  (let [common {:fill fill :stroke (token-var 'ground) :strokeWidth 1.5}]
    (case shape
      :square  [:rect (merge common {:x (- x rr) :y (- y rr) :width (* 2 rr) :height (* 2 rr) :rx 2})]
      :diamond [:polygon (merge common {:points (str x "," (- y rr) " " (+ x rr) "," y " "
                                                     x "," (+ y rr) " " (- x rr) "," y)})]
      :pill    [:rect (merge common {:x (- x (* rr 1.7)) :y (- y (* rr 0.7))
                                     :width (* rr 3.4) :height (* rr 1.4) :rx (* rr 0.7)})]
      [:circle (merge common {:cx x :cy y :r rr})])))

;; ---------------------------------------------------------------------------
;; The canvas — paint, camera and hit test
;; ---------------------------------------------------------------------------

(defn- css-value [^js el token]
  (when (and el token)
    (let [v (str/trim (.getPropertyValue (js/getComputedStyle el) (str "--" (name token))))]
      (when-not (str/blank? v) v))))

(defn- screen-radius
  "A node's radius in pixels. It grows with the zoom, slower than the zoom, so
   2,000 nodes are dots and a close look gives marks a hand can hit."
  [rr scale]
  (max 2.5 (min 18 (* rr (js/Math.sqrt scale)))))

(defn- sx-of [^js st wx] (let [^js cam (.-cam st)] (+ (/ (.-w st) 2) (* (- wx (.-x cam)) (.-s cam)))))
(defn- sy-of [^js st wy] (let [^js cam (.-cam st)] (+ (/ (.-h st) 2) (* (- wy (.-y cam)) (.-s cam)))))

(defn- to-world [^js st sx sy]
  (let [^js cam (.-cam st) s (.-s cam)]
    [(+ (.-x cam) (/ (- sx (/ (.-w st) 2)) s))
     (+ (.-y cam) (/ (- sy (/ (.-h st) 2)) s))]))

(defn- view-range
  "[low high] of the first n values of a typed array. Above 50 values it is
   the 2nd and the 98th percentile, so a few nodes far out do not shrink the
   whole picture to a dot. Below, it is the extent."
  [xs n]
  (if (<= n 50)
    (extent xs n)
    (let [v (.sort (.slice xs 0 n))
          k (js/Math.floor (* 0.02 n))]
      [(aget v k) (aget v (- n 1 k))])))

(defn- fit!
  "Put the picture in the pane: all of it, or for a large picture all but the
   few nodes furthest out."
  [^js st]
  (let [^js sim (.-sim st) n (.-n sim) xs (.-x sim) ys (.-y sim)]
    (if (zero? n)
      (set! (.-cam st) #js {:x 0 :y 0 :s 1})
      (let [[x0 x1] (view-range xs n) [y0 y1] (view-range ys n)
            bw (+ 140 (- x1 x0)) bh (+ 140 (- y1 y0))
            s  (min (/ (max 50 (.-w st)) bw) (/ (max 50 (.-h st)) bh) 3)]
        (set! (.-cam st) #js {:x (/ (+ x0 x1) 2) :y (/ (+ y0 y1) 2) :s s})))))

(defn- fit!*
  "fit! and the zoom it gives as the base of the zoom limits."
  [^js st]
  (fit! st)
  (set! (.-base_s st) (.-s (.-cam st))))

(defn- hit
  "The index of the node under a point of the screen, or nil. The last painted
   node wins, as the eye expects."
  [^js st sx sy]
  (when-let [^js sim (.-sim st)]
    (let [xs (.-x sim) ys (.-y sim) s (.-s (.-cam st)) styles (.-styles st)]
      (loop [i (dec (.-n sim))]
        (when-not (neg? i)
          (let [dx (- sx (sx-of st (aget xs i))) dy (- sy (sy-of st (aget ys i)))
                rr (+ 3 (screen-radius (:r (nth styles i)) s))]
            (if (<= (+ (* dx dx) (* dy dy)) (* rr rr))
              i
              (recur (dec i)))))))))

(defn- path-shape! [^js ctx shape x y rr]
  (case shape
    :square  (.rect ctx (- x rr) (- y rr) (* 2 rr) (* 2 rr))
    :diamond (do (.moveTo ctx x (- y rr)) (.lineTo ctx (+ x rr) y)
                 (.lineTo ctx x (+ y rr)) (.lineTo ctx (- x rr) y) (.closePath ctx))
    :pill    (let [w (* rr 1.7) h (* rr 0.7)]
               (.moveTo ctx (+ (- x w) h) (- y h))
               (.lineTo ctx (- (+ x w) h) (- y h))
               (.arc ctx (- (+ x w) h) y h (- (/ js/Math.PI 2)) (/ js/Math.PI 2))
               (.lineTo ctx (+ (- x w) h) (+ y h))
               (.arc ctx (+ (- x w) h) y h (/ js/Math.PI 2) (* 1.5 js/Math.PI))
               (.closePath ctx))
    (do (.moveTo ctx (+ x rr) y) (.arc ctx x y rr 0 (* 2 js/Math.PI)))))

(defn- label-of [nodes i]
  (let [l (str (:label (nth nodes i)))]
    (if (> (count l) 32) (str (subs l 0 32) "…") l)))

(defn- labelled
  "Which nodes carry their label. The focus, the hovered, the picked and the
   found always. Then the first nodes of the list — the query orders a set
   scope by degree, so these are the most connected — more of them as the
   person zooms in. Every node labelled at once is a wall of words."
  [^js st nodes]
  (let [n (count nodes) s (.-s (.-cam st)) base (.-base_s st)
        z (/ s (max 1e-6 base))
        k (if (<= n 40) n (min n (js/Math.floor (* 15 z z))))
        always (remove nil? [(.-hover st) (.-picked st) (.-found st)])]
    (distinct (concat (keep-indexed (fn [i nd] (when (:focus? nd) i)) nodes)
                      always
                      (range k)))))

(defn- paint!
  "Paint the scene on the canvas: the edges first, grouped by style so each
   style is one path, then the nodes, then the rings of the marked nodes, then
   the labels."
  [^js st]
  (when-let [^js cv (.-canvas st)]
    (when-let [^js sim (.-sim st)]
      (let [^js ctx (.getContext cv "2d")
            dpr (.-dpr st) w (.-w st) h (.-h st)
            xs (.-x sim) ys (.-y sim) es (.-es sim)
            s  (.-s (.-cam st))
            nodes (.-nodes st) styles (.-styles st)
            cache (atom {})
            color (fn [t fallback]
                    (or (get @cache t)
                        (let [v (or (css-value cv t) fallback)] (swap! cache assoc t v) v)))]
        (.setTransform ctx dpr 0 0 dpr 0 0)
        (set! (.-fillStyle ctx) (color 'panel "#ffffff"))
        (.fillRect ctx 0 0 w h)
        ;; EDGES, one path per style
        (set! (.-globalAlpha ctx) 0.55)
        (doseq [[{:keys [tone rule dash]} idxs] (when-not (.-hide_edges st) (.-egroups st))]
          (set! (.-strokeStyle ctx) (color tone "#888888"))
          (set! (.-lineWidth ctx) (or (some-> (css-value cv rule) js/parseFloat) 1))
          (.setLineDash ctx (clj->js (or (when (.-dashes st) dash) [])))
          (.beginPath ctx)
          (doseq [j idxs]
            (let [a (aget es (* 2 j)) b (aget es (inc (* 2 j)))]
              (.moveTo ctx (sx-of st (aget xs a)) (sy-of st (aget ys a)))
              (.lineTo ctx (sx-of st (aget xs b)) (sy-of st (aget ys b)))))
          (.stroke ctx))
        (.setLineDash ctx #js [])
        (set! (.-globalAlpha ctx) 1)
        ;; NODES, one path per style
        (set! (.-strokeStyle ctx) (color 'ground "#ffffff"))
        (set! (.-lineWidth ctx) 1.2)
        (doseq [[{:keys [tone shape r]} idxs] (.-ngroups st)]
          (set! (.-fillStyle ctx) (color tone "#4466cc"))
          (.beginPath ctx)
          (let [rr (screen-radius r s)]
            (doseq [i idxs]
              (let [px (sx-of st (aget xs i)) py (sy-of st (aget ys i))]
                (when (and (> px -20) (< px (+ w 20)) (> py -20) (< py (+ h 20)))
                  (path-shape! ctx shape px py rr)))))
          (.fill ctx) (.stroke ctx))
        ;; THE MARKED NODES: found, picked, hovered — a ring around each
        (doseq [[i tone] [[(.-found st) 'warn] [(.-picked st) 'sel] [(.-hover st) 'text]]
                :when (some? i)]
          (let [px (sx-of st (aget xs i)) py (sy-of st (aget ys i))
                rr (+ 4 (screen-radius (:r (nth styles i)) s))]
            (set! (.-strokeStyle ctx) (color tone "#222222"))
            (set! (.-lineWidth ctx) 2)
            (.beginPath ctx) (.arc ctx px py rr 0 (* 2 js/Math.PI)) (.stroke ctx)))
        ;; LABELS
        (set! (.-placed st) #js [])
        (let [font (or (.-fontFamily (js/getComputedStyle cv)) "sans-serif")]
          (set! (.-textAlign ctx) "center")
          (set! (.-textBaseline ctx) "top")
          (set! (.-lineJoin ctx) "round")
          ;; A LABEL THAT WOULD COVER A LABEL ALREADY PAINTED IS LEFT OUT. The
          ;; marked nodes come first in `labelled`, so they always win.
          (doseq [i (labelled st nodes)
                  :let [placed (.-placed st)]]
            (let [px (sx-of st (aget xs i)) py (sy-of st (aget ys i))
                  nd (nth nodes i)
                  strong? (or (:focus? nd) (= i (.-hover st)) (= i (.-picked st)) (= i (.-found st)))
                  rr (screen-radius (:r (nth styles i)) s)
                  l  (label-of nodes i)
                  _  (set! (.-font ctx) (str (if strong? "600 12px " "11px ") font))
                  lw (.-width (.measureText ctx l))
                  x0 (- px (/ lw 2)) y0 (+ py rr 3)
                  free? (or strong?
                            (not (some (fn [^js q] (and (< x0 (+ (aget q 0) (aget q 2)))
                                                        (< (aget q 0) (+ x0 lw))
                                                        (< y0 (+ (aget q 1) 14))
                                                        (< (aget q 1) (+ y0 14))))
                                       (array-seq placed))))]
              (when (and free? (> px -100) (< px (+ w 100)) (> py -20) (< py (+ h 20)))
                (.push placed #js [x0 y0 lw])
                (set! (.-lineWidth ctx) 3)
                (set! (.-strokeStyle ctx) (color 'panel "#ffffff"))
                (.strokeText ctx l px (+ py rr 3))
                (set! (.-fillStyle ctx) (color (if strong? 'text 'muted) "#333333"))
                (.fillText ctx l px (+ py rr 3))))))))))

(defn- group-indexes
  "Indexes grouped by a key fn, so each group paints as one path."
  [f coll]
  (reduce-kv (fn [m i x] (update m (f x) (fnil conj []) i)) {} (vec coll)))

(declare request!)

(defn- frame!
  "One animation frame: a slice of the simulation, then a paint. It asks for
   the next frame while the simulation runs. The canvas carries two
   measurements as attributes, for a probe: the time to the first paint and
   the time until the layout settled, in milliseconds after the data came."
  [^js st]
  (set! (.-raf st) nil)
  (let [^js sim (.-sim st)
        ^js cv  (.-canvas st)
        t0 (js/performance.now)]
    ;; A LARGE PICTURE SETTLES WITH MORE STEPS IN EACH FRAME, and with no
    ;; links painted while it moves when it has more than dash-limit links. A
    ;; paint of 8,000 links costs more than the steps it would separate.
    (when (running? sim)
      (let [budget (if (> (.-n sim) sync-limit) 40 12)]
        (loop []
          (when (and (step! sim 1) (< (- (js/performance.now) t0) budget))
            (recur)))))
    (set! (.-hide_edges st) (and (running? sim) (> (count (.-kept st)) dash-limit)))
    ;; THE VIEW FOLLOWS THE LAYOUT UNTIL THE PERSON MOVES IT. A layout that
    ;; settles in frames grows past the box of its seed, so the camera fits the
    ;; picture again on each frame, until the first wheel, drag or search.
    (when (and (not (.-touched st)) (> (.-n sim) sync-limit))
      (fit!* st))
    (let [t1 (js/performance.now)]
      (paint! st)
      ;; WHERE THE TIME OF A FRAME GOES, for a probe: the steps and the paint,
      ;; summed since the data came, and the count of frames.
      (set! (.-n_frames st) (inc (or (.-n_frames st) 0)))
      (set! (.-step_ms st) (+ (or (.-step_ms st) 0) (- t1 t0)))
      (set! (.-paint_ms st) (+ (or (.-paint_ms st) 0) (- (js/performance.now) t1)))
      (when cv
        (.setAttribute cv "data-frames" (.-n_frames st))
        (.setAttribute cv "data-step-ms" (js/Math.round (.-step_ms st)))
        (.setAttribute cv "data-paint-ms" (js/Math.round (.-paint_ms st)))
        (.setAttribute cv "data-steps" (.-step sim))))
    (when (and cv (not (.-first_paint st)))
      (set! (.-first_paint st) true)
      (.setAttribute cv "data-first-paint-ms" (js/Math.round (- (js/performance.now) (.-t_load st)))))
    (if (running? sim)
      (request! st)
      (when (and cv (not (.-settled st)))
        (set! (.-settled st) true)
        (.setAttribute cv "data-settled-ms" (js/Math.round (- (js/performance.now) (.-t_load st))))))))

(defn- request! [^js st]
  (when (and (nil? (.-raf st)) (.-canvas st))
    (set! (.-raf st) (js/requestAnimationFrame #(frame! st)))))

(defn- load!
  "Take a new picture: build its simulation, its styles and its groups. A
   small :force picture runs to its end here. A large one runs in frames."
  [^js st subgraph props-layout]
  (set! (.-t_load st) (js/performance.now))
  (let [{:keys [nodes edges]} subgraph
        layout (layout-for subgraph props-layout)
        ^js sim (sim-for layout nodes edges)
        _      (when (<= (count nodes) sync-limit) (while (step! sim 50)))
        styles (mapv (fn [nd] (assoc (node-style nd) :r (radius-of nd))) nodes)
        index  (.-index sim)
        kept   (into [] (filter (fn [e] (and (get index (:from e)) (get index (:to e))
                                             (not= (:from e) (:to e)))))
                     edges)]
    (set! (.-first_paint st) false)
    (set! (.-settled st) false)
    (set! (.-n_frames st) 0) (set! (.-step_ms st) 0) (set! (.-paint_ms st) 0)
    (when-let [^js cv (.-canvas st)]
      (.removeAttribute cv "data-first-paint-ms")
      (.removeAttribute cv "data-settled-ms"))
    (set! (.-sim st) sim)
    (set! (.-nodes st) (vec nodes))
    (set! (.-styles st) styles)
    (set! (.-ngroups st) (group-indexes #(select-keys % [:tone :shape :r]) styles))
    (set! (.-egroups st) (group-indexes #(select-keys (edge-style %) [:tone :rule :dash]) kept))
    (set! (.-kept st) kept)
    (set! (.-dashes st) (<= (count kept) dash-limit))
    (set! (.-hover st) nil) (set! (.-picked st) nil) (set! (.-found st) nil)
    (set! (.-touched st) false)
    (fit! st)
    (set! (.-base_s st) (.-s (.-cam st)))
    (request! st)))

(defn- resize! [^js st]
  (when-let [^js cv (.-canvas st)]
    (let [^js parent (.-parentNode cv)
          w (max 10 (.-clientWidth parent)) h (max 10 (.-clientHeight parent))
          dpr (or js/window.devicePixelRatio 1)]
      (when (or (not= w (.-w st)) (not= h (.-h st)))
        (set! (.-w st) w) (set! (.-h st) h) (set! (.-dpr st) dpr)
        (set! (.-width cv) (* w dpr)) (set! (.-height cv) (* h dpr))
        (when (.-sim st)
          (fit! st)
          (set! (.-base_s st) (.-s (.-cam st))))
        (request! st)))))

(defn- centre-on!
  "Move the view to one node, and zoom in so its label shows."
  [^js st i]
  (when-let [^js sim (.-sim st)]
    (let [^js cam (.-cam st)]
      (set! (.-x cam) (aget (.-x sim) i))
      (set! (.-y cam) (aget (.-y sim) i))
      (set! (.-s cam) (max (.-s cam) (* 2.5 (.-base_s st))))
      (set! (.-found st) i)
      (set! (.-touched st) true)
      (request! st))))

(defn- pointer [^js e ^js cv]
  (let [rect (.getBoundingClientRect cv)]
    [(- (.-clientX e) (.-left rect)) (- (.-clientY e) (.-top rect))]))

(defn- submit! [props bus session-id node]
  (let [submit (bind/submit-template props)]
    (when (and submit bus node)
      (js-invoke bus "dispatch"
                 (bind/fill-command submit {'$session-id session-id '$value (:id node)})
                 (fn [_] nil)))))

(defn- attach-events!
  "The gestures of the canvas. A wheel zooms about the pointer. A drag on a
   node moves the node, and the node stays there. A drag on the plane pans. A
   press and release on a node with no move is a click: it is the :submit."
  [^js st ^js cv get-props]
  (let [down (atom nil)]
    (.addEventListener cv "wheel"
      (fn [^js e]
        (.preventDefault e)
        (set! (.-touched st) true)
        (let [[sx sy] (pointer e cv)
              [wx wy] (to-world st sx sy)
              ^js cam (.-cam st)
              f   (if (pos? (.-deltaY e)) 0.88 1.136)
              s2  (max (* 0.2 (.-base_s st)) (min (* 40 (.-base_s st)) (* f (.-s cam))))]
          (set! (.-s cam) s2)
          ;; the world point under the pointer stays under the pointer
          (set! (.-x cam) (- wx (/ (- sx (/ (.-w st) 2)) s2)))
          (set! (.-y cam) (- wy (/ (- sy (/ (.-h st) 2)) s2)))
          (request! st)))
      #js {:passive false})
    (.addEventListener cv "pointerdown"
      (fn [^js e]
        (let [[sx sy] (pointer e cv)]
          (try (.setPointerCapture cv (.-pointerId e)) (catch :default _ nil))
          (reset! down {:i (hit st sx sy) :sx sx :sy sy :lx sx :ly sy :moved false}))))
    (.addEventListener cv "pointermove"
      (fn [^js e]
        (let [[sx sy] (pointer e cv)]
          (if-let [{:keys [i] :as d} @down]
            (let [moved (or (:moved d)
                            (> (+ (js/Math.abs (- sx (:sx d))) (js/Math.abs (- sy (:sy d)))) 4))]
              (when moved
                (if (some? i)
                  (let [^js sim (.-sim st) [wx wy] (to-world st sx sy)]
                    (aset (.-x sim) i wx) (aset (.-y sim) i wy) (aset (.-pin sim) i 1))
                  (let [^js cam (.-cam st) s (.-s cam)]
                    (set! (.-x cam) (- (.-x cam) (/ (- sx (:lx d)) s)))
                    (set! (.-y cam) (- (.-y cam) (/ (- sy (:ly d)) s)))))
                (set! (.-touched st) true)
                (reset! down (assoc d :moved true :lx sx :ly sy))
                (set! (.. cv -style -cursor) "grabbing")
                (request! st)))
            (let [i (hit st sx sy)]
              (set! (.. cv -style -cursor) (if (some? i) "pointer" "grab"))
              (when (not= i (.-hover st))
                (set! (.-hover st) i)
                (request! st)))))))
    (.addEventListener cv "pointerup"
      (fn [_]
        (let [d @down]
          (reset! down nil)
          (set! (.. cv -style -cursor) "grab")
          (when (and d (some? (:i d)) (not (:moved d)))
            (set! (.-picked st) (:i d))
            (request! st)
            (let [[props bus session-id] (get-props)]
              (submit! props bus session-id (nth (.-nodes st) (:i d))))))))
    (.addEventListener cv "pointerleave"
      (fn [_] (when (some? (.-hover st)) (set! (.-hover st) nil) (request! st))))))

;; ---------------------------------------------------------------------------
;; The widget
;; ---------------------------------------------------------------------------

(defn- data-key
  "What makes two pictures the same picture: the ids of the nodes and the
   edges, and the layout. A re-read that answers the same data keeps the view
   and every node that the person dragged."
  [subgraph props-layout]
  [(layout-for subgraph props-layout)
   (mapv :id (:nodes subgraph))
   (mapv (juxt :from :to :relation) (:edges subgraph))])

(defn- search-box
  "THE SEARCH BOX. It finds a node by its label and moves the view to it. It
   opens nothing: the open is the :submit gesture, and a search is not a
   choice."
  [_st-atom _nodes]
  (let [text (r/atom "")]
    (fn [st-atom nodes]
      (let [ms  (search-matches nodes @text 8)
            go! (fn [nd]
                  (when-let [^js st @st-atom]
                    (when-let [^js sim (.-sim st)]
                      (when-let [i (get (.-index sim) (:id nd))]
                        (centre-on! st i)))))]
        [:div {:style {:padding "var(--space-1) var(--space-2)"}}
         [:input {:type "search" :value @text
                  :placeholder "Find a node by its label"
                  :data-import-id "graph-search"
                  :style {:width "100%" :maxWidth "28rem"
                          :font "inherit" :fontSize "var(--type-small-size)"
                          :padding "var(--space-1) var(--space-2)"
                          :border (str "1px solid " (token-var 'border))
                          :borderRadius "var(--radius-sm)"
                          :background (token-var 'ground) :color (token-var 'text)}
                  :onChange #(reset! text (.. % -target -value))
                  :onKeyDown (fn [^js e]
                               (when (and (= "Enter" (.-key e)) (seq ms))
                                 (go! (first ms))))}]
         (when (and (seq (str/trim @text)) (empty? ms))
           [:span {:style {:marginLeft "var(--space-2)" :color (token-var 'faint)
                           :fontSize "var(--type-micro-size)"}}
            "No node has this text in its label."])
         (when (seq ms)
           (into [:div {:data-import-id "graph-search-results"
                        :style {:display "flex" :flexWrap "wrap" :gap "var(--space-1)"
                                :marginTop "var(--space-1)"}}]
                 (for [nd ms]
                   ^{:key (:id nd)}
                   [:button {:type "button"
                             :data-node-id (:id nd)
                             :style {:font "inherit" :fontSize "var(--type-micro-size)"
                                     :padding "1px var(--space-2)" :cursor "pointer"
                                     :border (str "1px solid " (token-var 'border))
                                     :borderRadius "var(--radius-sm)"
                                     :background (token-var 'panel) :color (token-var 'text)}
                             :onClick #(go! nd)}
                    (let [l (str (:label nd))] (if (> (count l) 40) (str (subs l 0 40) "…") l))])))]))))

(defn widget-graph
  "The :graph kind. See meta-UI v2.1-SNAPSHOT graph.edn for the declaration.

   ZOOM AND PAN ARE THE READER'S, and they live nowhere. A wheel zooms and a
   drag moves the view; neither is persisted, because a graph pane's viewport is
   not a preference — it is where you happen to be looking this minute."
  [_props _bus _session-id]
  (let [st-atom (atom nil)
        cv-el   (atom nil)
        props-a (atom nil)
        ro      (atom nil)
        sync!   (fn [this]
                  (let [[_ props bus session-id] (r/argv this)
                        ^js cv @cv-el
                        ^js old @st-atom]
                    (reset! props-a [props bus session-id])
                    (when (and old (not= cv (.-canvas old)))
                      ;; THE CANVAS WENT AWAY (an empty picture) or came back new
                      (when-let [id (.-raf old)] (js/cancelAnimationFrame id))
                      (when-let [o @ro] (.disconnect o) (reset! ro nil))
                      (set! (.-canvas old) nil)
                      (reset! st-atom nil))
                    (when cv
                      (if-let [^js st @st-atom]
                        (let [k (data-key (:subgraph props) (:layout props))]
                          (when (not= k (.-key st))
                            (set! (.-key st) k)
                            (load! st (:subgraph props) (:layout props))))
                        (let [^js st #js {:canvas cv :w 0 :h 0 :dpr 1 :raf nil
                                          :cam #js {:x 0 :y 0 :s 1}}]
                          (reset! st-atom st)
                          (attach-events! st cv (fn [] @props-a))
                          (resize! st)
                          (set! (.-key st) (data-key (:subgraph props) (:layout props)))
                          (load! st (:subgraph props) (:layout props))
                          (when (exists? js/ResizeObserver)
                            (let [o (js/ResizeObserver. (fn [_] (resize! st)))]
                              (.observe o (.-parentNode cv))
                              (reset! ro o))))))))]
    (r/create-class
      {:display-name "widget-graph"
       :component-did-mount  (fn [this] (sync! this))
       :component-did-update (fn [this _old] (sync! this))
       :component-will-unmount
       (fn [_]
         (when-let [o @ro] (.disconnect o))
         (when-let [^js st @st-atom]
           (when-let [id (.-raf st)] (js/cancelAnimationFrame id))
           (set! (.-canvas st) nil)))
       :reagent-render
       (fn [{:keys [subgraph caption legend empty-label search]} _bus _session-id]
         (let [{:keys [nodes edges]} subgraph]
           (if (empty? nodes)
             [:div {:style {:padding "var(--space-3)"
                            :color (token-var 'faint)
                            :fontStyle "italic"
                            :fontSize "var(--type-micro-size)"}}
              (or empty-label "Nothing connects here yet.")]
             [:div {:style {:display "flex" :flexDirection "column"
                            :flex 1 :minHeight 0
                            :background (token-var 'panel)}}
              (when search [search-box st-atom nodes])
              [:div {:style {:position "relative" :flex 1 :minHeight "12rem"}}
               [:canvas {:data-graph-canvas "true"
                         :data-node-count (count nodes)
                         :data-edge-count (count edges)
                         :ref (fn [el] (reset! cv-el el))
                         :style {:position "absolute" :inset 0 :width "100%" :height "100%"
                                 :display "block" :cursor "grab" :touchAction "none"}}]]
              (when (not (false? caption))
                [:div {:data-import-id "graph-caption"
                       :style {:padding "var(--space-1) var(--space-2)"
                               :fontSize "var(--type-micro-size)"
                               :color (token-var 'faint)
                               :borderTop (str "1px solid " (token-var 'border))}}
                 (caption-text subgraph)])
              (when (not (false? legend))
                (into [:div {:style {:display "flex" :flexWrap "wrap" :gap "var(--space-2)"
                                     :padding "var(--space-1) var(--space-2)"
                                     :fontSize "var(--type-micro-size)"
                                     :color (token-var 'muted)}}]
                      (for [{:keys [kind label style]} (cond->> (legend-rows nodes edges)
                                                          (> (count edges) dash-limit)
                                                          (map #(update % :style dissoc :dash)))]
                        ^{:key (str kind "-" label)}
                        [:span {:style {:display "inline-flex" :alignItems "center" :gap "4px"}}
                         (if (= :node kind)
                           [:svg {:width 12 :height 12 :viewBox "-6 -6 12 12"}
                            (shape-svg (:shape style) 0 0 4 (:fill style))]
                           [:svg {:width 16 :height 8 :viewBox "0 0 16 8"}
                            [:line (cond-> {:x1 0 :y1 4 :x2 16 :y2 4
                                            :stroke (:stroke style) :strokeWidth 1.5}
                                     (:dash style) (assoc :strokeDasharray (str/join " " (:dash style))))]])
                         label])))
              (when (> (count edges) dash-limit)
                [:span {:data-import-id "graph-dash-note"
                        :style {:padding "0 var(--space-2) var(--space-1)"
                                :fontSize "var(--type-micro-size)"
                                :color (token-var 'faint)}}
                 (str "More than " (fmt dash-limit) " links: every link is drawn solid.")])])))})))
